import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaFileAlt, FaTachometerAlt, FaTools, FaImages } from 'react-icons/fa';

const Container = styled.div`
  padding: 2rem;
  background: linear-gradient(to right, #e0f7fa, #f1f8e9);
  min-height: 100vh;
`;

const StyledTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  text-transform: uppercase;
`;

const WidgetCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: 8px solid #1976d2;
  margin-bottom: 2rem;
`;

const DashboardLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-left: 8px solid #27ae60;
`;

const CardHeader = styled.div`
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #e8f5e9;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  color: #1b5e20;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CardContent = styled.div`
  padding: 1rem 1.5rem;
  background-color: #f9fbe7;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const LinkItem = styled.li`
  margin: 0.5rem 0;
`;

const StyledLink = styled.button`
  background: #27ae60;
  border: none;
  color: white;
  font-weight: 600;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-align: left;
  width: 100%;
  transition: background-color 0.3s;

  &:hover {
    background-color: #219653;
  }
`;

const iconMap = {
  'User Tools': <FaUser />,
  'Reports': <FaFileAlt />,
  'Dashboards': <FaTachometerAlt />,
  'Operations': <FaTools />,
  'Media': <FaImages />,
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});
  const [metrics, setMetrics] = useState({ updatesCount: null, inspectionsCount: { preTrip: 0, postTrip: 0 } });

  const toggleExpand = (category) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await fetch("https://us-central1-landcsape-app.cloudfunctions.net/aggregateWeeklyReport", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setMetrics({
          updatesCount: data.servicesWithDate ?? 0,
          inspectionsCount: data.inspectionsCount ?? { preTrip: 0, postTrip: 0 },
        });
      } catch (error) {
        console.error("❌ Error fetching widget metrics:", error);
      }
    };

    fetchMetrics();
  }, []);

  const categorizedDashboards = {
    'User Tools': [
      { label: 'User Management', path: '/user-management' },
      {
        label: 'Leave Anonymous Feedback',
        onClick: () => window.open('https://employee-safe-space.vercel.app/', '_blank'),
      },
      
    ],
    'Reports': [
      { label: 'All Services Updates', path: '/admin-job-list' },
      { label: 'Open Punch Lists', path: '/admin-punch-list' },
      { label: 'Vehicle Inspection', path: '/all-inpsections' },
      { label: 'All Incident Reports', path: '/incident-list' },
      {
        label: 'Submitted Chemical Applications',
        onClick: () => window.open('https://rotoloconsultants.quickbase.com/nav/app/brspiu9w6/table/brt8jnu22/action/td', '_blank'),
      },
    ],
    'Dashboards': [
      { label: 'Manager Dashboard', path: '/manager-dashboard' },
      { label: 'Supervisor Dashboard', path: '/supervisor-dashboard' },
      { label: 'Spray Tech Manager Dashboard', path: '/spray-manager-dash' },
      { label: 'Spray Tech Dashboard', path: '/spray-tech-dashboard' },
      { label: 'Incident Report Dashboard', path: '/saved-incident-reports' },
    ],
    'Operations': [
      { label: 'Bid Proposal', path: '/bid-proposal' },
      { label: 'Check Request Form', path: '/check-request-form' },
      { label: 'Email Incident Summary', path: '/incident-email-summary' },
    ],
    'Media': [
      { label: 'Marketing Images', path: '/image-gallery' },
    ],
  };

  return (
    <Container>
      <StyledTitle>Admin Dashboard</StyledTitle>

      {/* Top Widget/Activity Section */}
      <WidgetCard>
        <h3>📊 Weekly Report Summary</h3>
        <p><strong>Updates This Week:</strong> {metrics.updatesCount ?? 'Loading...'}</p>
        <p><strong>Pre-trip Inspections:</strong> {metrics.inspectionsCount.preTrip ?? 'Loading...'}</p>
        <p><strong>Post-trip Inspections:</strong> {metrics.inspectionsCount.postTrip ?? 'Loading...'}</p>
        <StyledLink onClick={() => navigate('/weekly-report')}>
          View Full Weekly Report
        </StyledLink>
      </WidgetCard>

      {/* Dashboard Section */}
      <DashboardLayout>
        {Object.entries(categorizedDashboards).map(([category, items], idx) => (
          <Card key={idx}>
            <CardHeader onClick={() => toggleExpand(category)}>
              <CardTitle>
                {iconMap[category]} {category}
              </CardTitle>
              <span>{expanded[category] ? '−' : '+'}</span>
            </CardHeader>
            {expanded[category] && (
              <CardContent>
                <LinkList>
                  {items.map((item, index) => (
                    <LinkItem key={index}>
                      <StyledLink
                        onClick={() => (item.onClick ? item.onClick() : navigate(item.path))}
                      >
                        {item.label}
                      </StyledLink>
                    </LinkItem>
                  ))}
                </LinkList>
              </CardContent>
            )}
          </Card>
        ))}
      </DashboardLayout>
    </Container>
  );
};

export default AdminDashboard;
